import {
  type AssignCompanyPlanBody,
  type BlockOutcome,
  type BulkCompanyActionBody,
  type BulkCompanyActionResponse,
  blockOutcomeDto,
  bulkCompanyActionResponseDto,
  type CompanyDetail,
  type CompanyListItem,
  type CompanyStatus,
  type CreateCompanyBody,
  companyDetailDto,
  companyListItemDto,
  publisherSlugAvailableDto,
} from '@planuze/admin-contracts';
import { z } from 'zod';
import {
  ApiError,
  buildQuery,
  cursorPageSchema,
  HTTP,
  mapHttpError,
  parseJson,
  readErrorCode,
  request,
} from './http.js';

/* Cliente REST das empresas (28-11, ADR-0354/0699). Usa os helpers compartilhados do
   `http.ts` (`buildQuery`/`cursorPageSchema`/`mapHttpError`/`HTTP`) — sem plumbing local. */

export interface CompaniesQuery {
  readonly search?: string | undefined;
  readonly status?: CompanyStatus | undefined;
  readonly cursor?: string | undefined;
}

const companiesPageSchema = cursorPageSchema(companyListItemDto);

/* Resposta da criação de org (admin-created — ADR-0421): o admin-service repassa o
   `{ org }` do auth; só precisamos de id+name (Zod descarta o resto da OrgRecord). */
const createdOrgSchema = z.object({ org: z.object({ id: z.string(), name: z.string() }) });

/* Erros do CRUD/governança de empresa: 404→not_found, 400→not_blocked (unblock inválido),
   409→grace_expired (janela de 24h, autoritativa no servidor). Demais → server_error. */
function companyError(res: Response): Promise<ApiError> {
  return mapHttpError(res, {
    [HTTP.badRequest]: 'not_blocked',
    [HTTP.conflict]: 'grace_expired',
  });
}

/* Erros da criação de org (admin-created): 403 sem permissão; 409 slug tomado (o provision é a
   autoridade final — ADR-0600); 413 logo acima do teto (ADR-0615); 400 owner_not_found/
   invalid_format/public_blocked/slug_taken/invalid_logo (domínio declarado pelo admin). */
async function createCompanyError(res: Response): Promise<ApiError> {
  const code = await readErrorCode(res);
  if (res.status === HTTP.forbidden) return new ApiError(res.status, 'unauthorized', 'forbidden');
  if (res.status === HTTP.conflict)
    return new ApiError(res.status, 'slug_taken', 'namespace taken');
  if (res.status === HTTP.payloadTooLarge) {
    return new ApiError(res.status, 'logo_too_large', 'logo too large');
  }
  if (res.status === HTTP.badRequest) {
    if (code === 'owner_not_found') {
      return new ApiError(res.status, 'owner_not_found', 'owner user not found');
    }
    if (
      code === 'invalid_format' ||
      code === 'public_blocked' ||
      code === 'slug_taken' ||
      code === 'invalid_logo'
    ) {
      return new ApiError(res.status, code, code);
    }
  }
  return new ApiError(res.status, 'server_error', code || 'create failed');
}

/* Erros da atribuição do plano da org (C6): 403 sem permissão; 400/409 = lookup_key já em uso
   no Stripe / planKey já ativo. Demais → server_error. */
async function assignPlanError(res: Response): Promise<ApiError> {
  const code = await readErrorCode(res);
  if (res.status === HTTP.forbidden) return new ApiError(res.status, 'unauthorized', 'forbidden');
  if (res.status === HTTP.badRequest || res.status === HTTP.conflict) {
    return new ApiError(res.status, 'plan_key_taken', code || 'plan key already in use');
  }
  return new ApiError(res.status, 'server_error', code || 'assign plan failed');
}

/** GET /v1/admin/companies/publisher-slug-available — pré-check global do namespace (ADR-0600). */
export async function publisherSlugAvailable(slug: string): Promise<boolean> {
  const res = await request(
    `/v1/admin/companies/publisher-slug-available?slug=${encodeURIComponent(slug)}`,
  );
  if (!res.ok) throw new ApiError(res.status, 'server_error', 'slug check failed');
  return (await parseJson(res, publisherSlugAvailableDto)).available;
}

/** POST /v1/admin/companies — cria org admin-created designando um owner inicial. */
export async function createCompany(
  body: CreateCompanyBody,
): Promise<{ readonly id: string; readonly name: string }> {
  const res = await request('/v1/admin/companies', { method: 'POST', body });
  if (!res.ok) throw await createCompanyError(res);
  return (await parseJson(res, createdOrgSchema)).org;
}

/** POST /v1/admin/companies/:id/assign-plan — define o plano custom da org. */
export async function assignCompanyPlan(
  companyId: string,
  body: AssignCompanyPlanBody,
): Promise<void> {
  const res = await request(`/v1/admin/companies/${encodeURIComponent(companyId)}/assign-plan`, {
    method: 'POST',
    body,
  });
  if (!res.ok) throw await assignPlanError(res);
}

/** GET /v1/admin/companies — lista paginada por cursor. */
export async function listCompanies(
  query: CompaniesQuery,
): Promise<{ readonly items: readonly CompanyListItem[]; readonly nextCursor: string | null }> {
  const res = await request(
    `/v1/admin/companies${buildQuery({ search: query.search, status: query.status, cursor: query.cursor })}`,
  );
  if (!res.ok) throw await companyError(res);
  const page = await parseJson(res, companiesPageSchema);
  return { items: page.items, nextCursor: page.nextCursor ?? null };
}

/** GET /v1/admin/companies/:id — detalhe agregado (members + subscription + audit). */
export async function getCompany(id: string): Promise<CompanyDetail> {
  const res = await request(`/v1/admin/companies/${encodeURIComponent(id)}`);
  if (!res.ok) throw await companyError(res);
  return parseJson(res, companyDetailDto);
}

/** POST /v1/admin/companies/:id/block — cascade (revoga sessões + pausa billing). */
export async function blockCompany(id: string, reason: string): Promise<BlockOutcome> {
  const res = await request(`/v1/admin/companies/${encodeURIComponent(id)}/block`, {
    method: 'POST',
    body: { reason },
  });
  if (!res.ok) throw await companyError(res);
  return parseJson(res, blockOutcomeDto);
}

/** POST /v1/admin/companies/:id/unblock — 409 ⇒ grace_expired (usar emissão manual). */
export async function unblockCompany(id: string, reason: string): Promise<BlockOutcome> {
  const res = await request(`/v1/admin/companies/${encodeURIComponent(id)}/unblock`, {
    method: 'POST',
    body: { reason },
  });
  if (!res.ok) throw await companyError(res);
  return parseJson(res, blockOutcomeDto);
}

/** POST /v1/admin/companies/bulk — block/unblock homogêneo com resultado por empresa. */
export async function bulkCompanyAction(
  body: BulkCompanyActionBody,
): Promise<BulkCompanyActionResponse> {
  const res = await request('/v1/admin/companies/bulk', { method: 'POST', body });
  if (!res.ok) throw await companyError(res);
  return parseJson(res, bulkCompanyActionResponseDto);
}
